import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, Tag } from '@/types';
import { X, Upload, Wand2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { getApiBase } from '@/utils/storage';
import ImageWithLoader from '@/components/ImageWithLoader';

interface BookmarkEditModalProps {
    bookmark: Bookmark | null;
    tags: Tag[];
    isOpen: boolean;
    onClose: () => void;
    onSave: (bookmark: Bookmark) => void;
}

const BookmarkEditModal = ({ bookmark, tags, isOpen, onClose, onSave }: BookmarkEditModalProps) => {
    const [formData, setFormData] = useState<Bookmark>({
        id: '',
        title: '',
        url: '',
        favicon: '',
        thumbnail: '',
        tags: [],
        createdAt: 0,
        visitCount: 0,
        notes: ''
    });

    useEffect(() => {
        if (bookmark) {
            setFormData(bookmark);
        } else {
            setFormData({
                id: uuidv4(),
                title: '',
                url: '',
                favicon: '',
                thumbnail: '',
                tags: [],
                createdAt: Date.now(),
                visitCount: 0,
                notes: ''
            });
        }
    }, [bookmark, isOpen]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, field: 'favicon' | 'thumbnail') => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) { // Limit to 2MB
                alert('图片大小不能超过 2MB');
                return;
            }
            
            // Check for Local Mode
            const { isLocalMode } = await import('@/utils/storage');
            
            if (isLocalMode()) {
                // In Local Mode, convert file to Base64 and save locally
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result as string;
                    // Check if base64 string is too large for localStorage?
                    // LocalStorage has ~5MB limit. A 2MB image might fill it quickly.
                    // But user requested this feature.
                    if (base64String.length > 3 * 1024 * 1024) {
                        alert('图片过大，转换后超过存储限制，建议使用 URL 或压缩图片。');
                        return;
                    }
                    setFormData(prev => ({ ...prev, [field]: base64String }));
                };
                reader.onerror = () => {
                    alert('读取文件失败');
                };
                reader.readAsDataURL(file);
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
                // Get configured API Base
                // Note: getApiBase now reads from localStorage dynamically
                const apiBase = getApiBase();
                // Extract base URL from apiBase (remove /api)
                const baseUrl = apiBase.replace(/\/api$/, '');
                
                // Note: Even in Local Mode, uploads must go to a server because we can't save files to localStorage directly.
                // We use the configured API Base (which might be localhost:3014 or a remote server).
                const response = await fetch(`${baseUrl}/upload`, {
                    method: 'POST',
                    body: formData
                });
                
                if (!response.ok) {
                    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
                }
                
                const data = await response.json();
                setFormData(prev => ({ ...prev, [field]: data.url }));
            } catch (error) {
                console.error('Upload error:', error);
                alert(`上传失败: ${error instanceof Error ? error.message : '未知错误'}\n请确保服务端 (${getApiBase()}) 已启动并可访问。`);
            }
        }
    };

    const toggleTag = (tagId: string) => {
        setFormData(prev => {
            const newTags = prev.tags.includes(tagId)
                ? prev.tags.filter(id => id !== tagId)
                : [...prev.tags, tagId];
            return { ...prev, tags: newTags };
        });
    };

    const handleAutoScreenshot = async () => {
        if (!formData.url) {
            alert('请先输入网址');
            return;
        }

        try {
            // Get custom screenshot API from settings
            const settings = await import('@/utils/storage').then(m => m.getSettings());
            const screenshotApi = settings?.screenshotApiUrl;
            
            // Use custom API if configured, otherwise use default
            const baseUrl = screenshotApi 
                ? screenshotApi 
                : 'http://119.91.119.114:3015/api/convert/screenshot?url=';
            
            // Ensure proper concatenation
            const targetUrl = baseUrl.endsWith('=') ? baseUrl : `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}url=`;
            const fullApiUrl = `${targetUrl}${encodeURIComponent(formData.url)}`;
            
            // Set directly as thumbnail URL
            setFormData(prev => ({ ...prev, thumbnail: fullApiUrl }));
            
        } catch (error) {
            console.error('Screenshot error:', error);
            alert('获取截图失败');
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 p-6 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">{bookmark ? '编辑收藏' : '添加收藏'}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900 dark:hover:text-white"><X size={20} /></button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">标题</label>
                        <input 
                            type="text" 
                            value={formData.title}
                            onChange={e => setFormData({...formData, title: e.target.value})}
                            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">URL</label>
                        <input 
                            type="text" 
                            value={formData.url}
                            onChange={e => setFormData({...formData, url: e.target.value})}
                            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">备注 (可选)</label>
                        <textarea
                            value={formData.notes || ''}
                            onChange={e => setFormData({...formData, notes: e.target.value})}
                            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400 resize-none"
                            rows={3}
                            placeholder="记录附加信息，如用途、来源、注意事项等"
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-2">标签</label>
                        <div className="flex flex-wrap gap-2">
                            {tags.map(tag => (
                                <button
                                    key={tag.id}
                                    onClick={() => toggleTag(tag.id)}
                                    className={`px-3 py-1 rounded-full text-xs transition-all border ${
                                        formData.tags.includes(tag.id)
                                            ? 'text-white border-transparent'
                                            : 'bg-transparent text-slate-500 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                                    }`}
                                    style={{
                                        backgroundColor: formData.tags.includes(tag.id) ? tag.color : undefined
                                    }}
                                >
                                    {tag.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">图标 URL (可选)</label>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={formData.favicon}
                                onChange={e => setFormData({...formData, favicon: e.target.value})}
                                className="flex-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                                placeholder="留空自动获取或上传"
                            />
                            <label className="cursor-pointer flex items-center justify-center px-3 py-2 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors" title="上传图标">
                                <Upload size={16} className="text-slate-500 dark:text-slate-400" />
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, 'favicon')} />
                            </label>
                        {formData.favicon && (
                            <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 shrink-0">
                                <ImageWithLoader src={formData.favicon} alt="Favicon Preview" className="w-full h-full" />
                            </div>
                        )}
                        </div>
                    </div>
                     <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">缩略图 URL (可选)</label>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={formData.thumbnail}
                                onChange={e => setFormData({...formData, thumbnail: e.target.value})}
                                className="flex-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                                placeholder="图片 URL 或上传"
                            />
                            <button 
                                onClick={handleAutoScreenshot}
                                className="flex items-center justify-center px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors text-blue-600 dark:text-blue-400"
                                title="自动获取截图"
                            >
                                <Wand2 size={16} />
                            </button>
                             <label className="cursor-pointer flex items-center justify-center px-3 py-2 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors" title="上传缩略图">
                                <Upload size={16} className="text-slate-500 dark:text-slate-400" />
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, 'thumbnail')} />
                            </label>
                            {formData.thumbnail && (
                                <div className="w-16 h-10 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 shrink-0">
                                    <ImageWithLoader src={formData.thumbnail} alt="Thumbnail Preview" className="w-full h-full" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end mt-6 gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white text-sm">取消</button>
                    <button 
                        onClick={() => onSave(formData)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm"
                    >
                        保存
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default BookmarkEditModal;
